import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Sidebar from './Sidebar';
import Header from './Header';
import DashboardStats from './DashboardStats';
import Organizations from './Organizations';
import Users from './Users';
import PendingUsers from './PendingUsers';
import Contents from './Contents';
import Flows from './Flows';
import ApiKeys from './ApiKeys';
import Visibility from './Visibility';
import SiteCollections from './SiteCollections';
import SurveyInsights from './SurveyInsights';
import UserProfileSidebar from './UserProfileSidebar';
import IapUsageAnalyticsHub from '../analytics/iapUsage/IapUsageAnalyticsHub';
import EndUserAnalyticsHub from '../analytics/endUser/EndUserAnalyticsHub';
import Metering from './Metering';
import EnrollmentKeys from './EnrollmentKeys';
import FeatureGate from './shared/FeatureGate';
import {
  IapUsageSkeleton,
  CustomerAnalyticsSkeleton,
  HeatmapSkeleton,
  SurveyInsightsSkeleton
} from './shared/skeletons';
import './styles/Dashboard.css';

import { trackEvent } from '../services/applicationInsights';
import { parseJwt } from '../auth/jwt';
import * as meteringService from '../services/metering.service';
import { getCaptureRequirements } from '../services/telemetry.service';
import { NAV_LINKS, ANALYTICS_NAV_LINKS, isNavItemAllowed } from './navConfig';

import { useAuth } from '../auth/AuthProvider';

export default function Dashboard() {
  const { tokens, logout, isAuthenticated } = useAuth();
  const token = tokens?.access_token;

  const [user, setUser] = useState(() => (token ? parseJwt(token) : null));
  const [roles, setRoles] = useState(() => {
    if (!token) return [];
    const decoded = parseJwt(token);
    if (!decoded) return [];
    const rolesFromToken =
      decoded.role ||
      decoded.roles ||
      decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
      [];
    return Array.isArray(rolesFromToken) ? rolesFromToken : [rolesFromToken];
  });
  const [orgId, setOrgId] = useState(() => {
    if (!token) return '';
    const decoded = parseJwt(token);
    return decoded?.org_id || '';
  });
  const [isIdentityResolved, setIsIdentityResolved] = useState(() => Boolean(token));
  const [activeSection, setActiveSection] = useState('dashboard');
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('iap_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const handleToggleSidebarCollapse = () => {
    setIsSidebarCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem('iap_sidebar_collapsed', String(next));
      } catch {
        // ignore storage error
      }
      return next;
    });
  };
  const [navParams, setNavParams] = useState(null);
  const [cachedStats, setCachedStats] = useState(null);
  const [cacheTimestamp, setCacheTimestamp] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [entitlementsLoaded, setEntitlementsLoaded] = useState(false);

  useEffect(() => {
    if (!token) {
      setUser(null);
      setRoles([]);
      setOrgId('');
      setIsIdentityResolved(false);
      return;
    }

    const decoded = parseJwt(token);
    if (!decoded) {
      logout();
      return;
    }

    setUser(decoded);

    const rolesFromToken =
      decoded.role ||
      decoded.roles ||
      decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
      [];
    setRoles(Array.isArray(rolesFromToken) ? rolesFromToken : [rolesFromToken]);
    setOrgId(decoded.org_id || '');
    setIsIdentityResolved(true);

  }, [token, isAuthenticated, logout]);

  const lastTrackedSectionRef = useRef(null);

  useEffect(() => {
    if (activeSection && activeSection !== lastTrackedSectionRef.current) {
      lastTrackedSectionRef.current = activeSection;
      trackEvent('Section View', { section: activeSection, username: user?.username, orgId });
    }
  }, [activeSection, user, orgId]);

  useEffect(() => {
    let isMounted = true;
    const targetOrgId = localStorage.getItem('selected_organization_id') || orgId;
    if (!targetOrgId) {
      setEntitlements(null);
      setEntitlementsLoaded(true);
      return;
    }
    setEntitlementsLoaded(false);
    const fetchOrgEntitlements = async () => {
      try {
        const entData = await meteringService.getEntitlements(targetOrgId);
        if (isMounted) {
          setEntitlements(entData);
        }
      } catch (err) {
        console.warn('Failed to load entitlements in Dashboard:', err);
      } finally {
        if (isMounted) {
          setEntitlementsLoaded(true);
        }
      }

      // Perform telemetry capability discovery handshake
      try {
        await getCaptureRequirements(targetOrgId);
      } catch (err) {
        console.warn('Failed telemetry capture-requirements handshake:', err);
      }
    };
    fetchOrgEntitlements();
    return () => { isMounted = false; };
  }, [orgId, isIdentityResolved]);

  const hasSurveyInsightsAccess = useMemo(() => {
    const normalized = roles.map(r => (typeof r === 'string' ? r.toLowerCase() : ''));
    return normalized.some(r =>
      r === 'systemadministrator' ||
      r === 'organizationadministrator' ||
      r === 'sitecollectionowner'
    );
  }, [roles]);

  const isSystemAdmin = useMemo(() => {
    return roles.some(r => typeof r === 'string' && r.toLowerCase() === 'systemadministrator');
  }, [roles]);

  const enabledFeatures = useMemo(() => {
    return Array.isArray(entitlements?.enabledFeatures) ? entitlements.enabledFeatures : [];
  }, [entitlements]);

  const navItemContext = useMemo(() => ({
    roles,
    enabledFeatures,
    isEntitlementsLoaded: entitlementsLoaded,
    isSystemAdmin
  }), [roles, enabledFeatures, entitlementsLoaded, isSystemAdmin]);

  const hasExtensionPlayerFeature = useMemo(() => {
    return isNavItemAllowed(NAV_LINKS.enrollmentkeys, navItemContext);
  }, [navItemContext]);

  const canAccessEnrollmentKeys = hasExtensionPlayerFeature;

  const hasSurveyInsightsFeature = useMemo(() => {
    const item = ANALYTICS_NAV_LINKS.find(i => i.id === 'survey-insights');
    return isNavItemAllowed(item, navItemContext);
  }, [navItemContext]);

  const hasAdvancedAnalyticsFeature = useMemo(() => {
    const item = ANALYTICS_NAV_LINKS.find(i => i.id === 'analytics-iap');
    return isNavItemAllowed(item, navItemContext);
  }, [navItemContext]);

  const hasHeatmapFeature = useMemo(() => {
    const item = ANALYTICS_NAV_LINKS.find(i => i.id === 'heatmap-analytics');
    return isNavItemAllowed(item, navItemContext);
  }, [navItemContext]);

  const hasCustomerAnalyticsFeature = useMemo(() => {
    const item = ANALYTICS_NAV_LINKS.find(i => i.id === 'analytics-customer');
    return isNavItemAllowed(item, navItemContext);
  }, [navItemContext]);

  const hasSessionReplayFeature = useMemo(() => {
    return enabledFeatures.includes('session_replay') || Boolean(hasHeatmapFeature);
  }, [enabledFeatures, hasHeatmapFeature]);

  useEffect(() => {
    if (!entitlementsLoaded) return;

    // RBAC-based redirects: users without proper role access cannot navigate to these sections
    if (activeSection === 'survey-insights' && !hasSurveyInsightsAccess) {
      setActiveSection('dashboard');
    }
    if (activeSection === 'enrollmentkeys' && !canAccessEnrollmentKeys) {
      setActiveSection('dashboard');
    }
  }, [activeSection, hasSurveyInsightsAccess, canAccessEnrollmentKeys, entitlementsLoaded]);

  const handleLogout = async () => {
    trackEvent('Logout', {
      username: user?.username,
      email: user?.email
    });

    await logout();
  };

  const handleNavigateWithParams = (section, params) => {
    setNavParams(params);
    setActiveSection(section);
  };

  const handleDataFetched = useCallback((data) => {
    setCachedStats(data);
    setCacheTimestamp(Date.now());
  }, []); // ← empty deps: setCachedStats and setCacheTimestamp are stable (from useState)

  const renderSection = () => {
    switch (activeSection) {
      case 'analytics-iap':
      case 'advanced-analytics':
      case 'iap-analytics':
        return (
          <FeatureGate
            entitled={hasAdvancedAnalyticsFeature}
            featureName="IAP Analytics"
            featureDescription="Advanced workflow adoption, user journey, and module usage metrics across your applications."
            skeletonContent={<IapUsageSkeleton featureName="IAP Analytics" />}
          >
            <IapUsageAnalyticsHub
              orgId={orgId}
              roles={roles}
              hasAdvancedAnalyticsFeature={hasAdvancedAnalyticsFeature}
            />
          </FeatureGate>
        );
      case 'customer-analytics':
      case 'analytics-customer':
      case 'behavioural-analytics':
      case 'behavioral-analytics':
      case 'journey-mining':
      case 'friction-signals':
      case 'session-replay':
        return (
          <FeatureGate
            entitled={hasCustomerAnalyticsFeature}
            featureName="Behavioural Analytics"
            featureDescription="Behavioral funnels, multi-dimensional audience breakdowns, and user journey paths."
            skeletonContent={<CustomerAnalyticsSkeleton />}
          >
            <EndUserAnalyticsHub
              orgId={orgId}
              roles={roles}
              activeSection={activeSection}
              hasHeatmapFeature={hasHeatmapFeature}
              hasCustomerAnalyticsFeature={hasCustomerAnalyticsFeature}
              hasSessionReplayFeature={hasSessionReplayFeature}
              onNavigate={handleNavigateWithParams}
            />
          </FeatureGate>
        );
      case 'heatmap-settings':
      case 'heatmap-analytics':
        return (
          <FeatureGate
            entitled={hasHeatmapFeature}
            featureName="Heatmap Analytics"
            featureDescription="User interaction heatmaps, click density grids, and session recording settings."
            skeletonContent={<HeatmapSkeleton />}
          >
            <EndUserAnalyticsHub
              orgId={orgId}
              roles={roles}
              activeSection={activeSection}
              hasHeatmapFeature={hasHeatmapFeature}
              hasCustomerAnalyticsFeature={hasCustomerAnalyticsFeature}
              hasSessionReplayFeature={hasSessionReplayFeature}
              onNavigate={handleNavigateWithParams}
            />
          </FeatureGate>
        );
      case 'dashboard':
        return (
          <DashboardStats
            user={user}
            orgId={orgId}
            token={token}
            roles={roles}
            onNavigate={handleNavigateWithParams}
            cachedData={cachedStats}
            onDataFetched={handleDataFetched}
            cacheTimestamp={cacheTimestamp}
          />
        );
      case 'organization':
        return <Organizations roles={roles} token={token} orgId={orgId} />;
      case 'users':
        return (
          <Users
            roles={roles}
            orgId={orgId}
            token={token}
            initialUserEmail={navParams?.email}
            initialFilterStatus={navParams?.filterStatus}
            onLoadComplete={() => setNavParams(null)}
          />
        );
      case 'pending':
        return <PendingUsers orgId={orgId} token={token} roles={roles} />;
      case 'sitecollections':
        return <SiteCollections token={token} roles={roles} orgId={orgId} orgContextResolved={isIdentityResolved} initialOrganizationId={navParams?.organizationId} onNavigate={handleNavigateWithParams} />;
      case 'contents':
        return <Contents token={token} roles={roles} orgId={orgId} />;
      case 'flows':
        return <Flows token={token} roles={roles} orgId={orgId} />;
      case 'visibility':
        return <Visibility token={token} roles={roles} orgId={orgId} />;
      case 'apikey':
      case 'apiKeys':
        return <ApiKeys token={token} roles={roles} orgId={orgId} />;
      case 'enrollmentkeys':
        if (!canAccessEnrollmentKeys) {
          return (
            <DashboardStats
              user={user}
              orgId={orgId}
              token={token}
              roles={roles}
              onNavigate={handleNavigateWithParams}
              cachedData={cachedStats}
              onDataFetched={handleDataFetched}
              cacheTimestamp={cacheTimestamp}
            />
          );
        }
        return <EnrollmentKeys token={token} roles={roles} orgId={orgId} onNavigate={handleNavigateWithParams} />;
      case 'survey-insights':
        if (!hasSurveyInsightsAccess) {
          return (
            <DashboardStats
              user={user}
              orgId={orgId}
              token={token}
              roles={roles}
              onNavigate={handleNavigateWithParams}
              cachedData={cachedStats}
              onDataFetched={handleDataFetched}
              cacheTimestamp={cacheTimestamp}
            />
          );
        }
        return (
          <FeatureGate
            entitled={hasSurveyInsightsFeature}
            featureName="Survey Insights"
            featureDescription="Deep user sentiment analysis, satisfaction scores, and feedback intelligence."
            skeletonContent={<SurveyInsightsSkeleton />}
          >
            <SurveyInsights token={token} roles={roles} orgId={orgId} />
          </FeatureGate>
        );
      case 'metering':
        return <Metering token={token} roles={roles} orgId={orgId} />;
      default:
        return (
          <DashboardStats
            user={user}
            orgId={orgId}
            token={token}
            roles={roles}
            onNavigate={handleNavigateWithParams}
            cachedData={cachedStats}
            onDataFetched={handleDataFetched}
            cacheTimestamp={cacheTimestamp}
          />
        );
    }
  };

  return (
    <div className="dashboard-shell">
      <Header
        currentUser={user}
        roles={roles}
        activeSection={activeSection}
        onProfileClick={() => setIsProfileOpen(true)}
        onToggleSidebar={() => {
          if (typeof window !== 'undefined' && window.innerWidth <= 1024) {
            setIsSidebarOpen(prev => !prev);
          } else {
            handleToggleSidebarCollapse();
          }
        }}
        isSidebarCollapsed={isSidebarCollapsed}
      />

      <div className="dashboard-layout">
        {/* Mobile/Tablet Sidebar Overlay */}
        {isSidebarOpen && (
          <div
            className="sidebar-overlay"
            onClick={() => setIsSidebarOpen(false)}
            aria-label="Close sidebar"
          />
        )}

        <Sidebar
          user={user}
          roles={roles}
          active={activeSection}
          onSelect={(section) => {
            setActiveSection(section);
            setIsSidebarOpen(false); // Close sidebar on mobile after selecting
          }}
          hasSurveyInsightsFeature={hasSurveyInsightsFeature}
          hasAdvancedAnalyticsFeature={hasAdvancedAnalyticsFeature}
          hasHeatmapFeature={hasHeatmapFeature}
          hasCustomerAnalyticsFeature={hasCustomerAnalyticsFeature}
          hasExtensionPlayerFeature={hasExtensionPlayerFeature}
          enabledFeatures={enabledFeatures}
          isEntitlementsLoaded={entitlementsLoaded}
          isOpen={isSidebarOpen}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={handleToggleSidebarCollapse}
          onClose={() => setIsSidebarOpen(false)}
        />

        <main className="main">
          {renderSection()}
        </main>
      </div>

      <UserProfileSidebar
        user={user}
        roles={roles}
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
        onLogout={handleLogout}
        onNavigate={handleNavigateWithParams}
      />
    </div>
  );
}
